import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useAtom } from 'jotai';
import React, { useState } from 'react';
import SidePanelForStep from './DroppableCanvas/WorkflowCanvasWithLayout/TreeLayout/SidePanelForStep/SidePanelForStep';
import SidePanel from './SidePanel/SidePanel';
import { nextStepGroupAtom, failbackStepGroupAtom } from './workflowFieldAtoms';
import { DraggableModules } from './DraggableModules/DraggableModules';
import DragOverlayModule from './DragOverlayModule/DragOverlayModule';
import DroppableCanvas from './DroppableCanvas/DroppableCanvas';
import { DropZoneProperties } from './DroppableCanvas/WorkflowCanvasWithLayout/DropZone/DropZone';
import {
  Parameter,
  ServerModule,
  ServerParameterGroup,
  ServerStep,
  ServerStepGroup,
} from './ServerWorkflowType';
import { generateNumericID } from './utils';
import {
  createAndAttachNextStepGroup,
  createPreviousStepGroup,
  findNodeById,
  findStepById,
} from './workflowTreeUtils';
import { useWorkflowById } from './hooks/useWorkflowById/useWorkflowById';

const CreateWorkflowPage = () => {
  const [nextGroup, setNextGroup] = useAtom(nextStepGroupAtom);

  const [failbackStepGroup, setFailbackStepGroup] = useAtom(
    failbackStepGroupAtom
  );
  const [activeModule, setActiveModule] = useState<ServerModule | null>(null);

  useWorkflowById();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragStart = (event: DragStartEvent) => {
    const draggedModule = event.active.data.current as ServerModule;
    setActiveModule(draggedModule);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveModule(null);
    const { active, over } = event;
    if (!over) {
      return;
    }

    const draggedModule = active.data.current as ServerModule;
    const dropZone = over.data.current as DropZoneProperties;
    const parametersGroups: ServerParameterGroup[] = [
      {
        groupCondition: null,
        groupTitle: null,
        paramGroupId: null,
        parameters: draggedModule.parameters.map((parameter): Parameter => {
          return {
            escSpecSymbols: false,
            isDynamic: false,
            paramGroupId: null,
            paramId: null,
            parameter,
            value: parameter.default,
          };
        }),
      },
    ];
    const generatedWorkflowStepGroupId = generateNumericID();
    const step: ServerStep = {
      failbackStepGroup: null,
      module: draggedModule,
      nestedStepGroup: null,
      paramGroups: parametersGroups,
      retryDelay: 0,
      retryDelayMultiplier: 0,
      retryNumber: 0,
      stepCondition: '',
      stepId: draggedModule.textId,
      stepIndex: '1',
      stepTitle: draggedModule.name,
      workflowStepGroupId: generatedWorkflowStepGroupId,
      workflowStepId: generateNumericID(),
    };

    const updatedGroup: ServerStepGroup = {
      nextStepGroup: null,
      parentWorkflowStepGroupId: dropZone.parentWorkflowStepGroupId ?? null,
      steps: [step],
      workflowStepGroupId: generatedWorkflowStepGroupId,
    };

    const mergeTrees = (
      oldTree: ServerStepGroup | null,
      treeToAdd: ServerStepGroup
    ) => {
      if (!oldTree) {
        return updatedGroup;
      }

      if (dropZone.isNextStepGroup) {
        return createAndAttachNextStepGroup(
          oldTree,
          updatedGroup,
          dropZone.workflowStepGroupId
        );
      }
      if (dropZone.isPreviousStepGroup) {
        return createPreviousStepGroup(
          oldTree,
          updatedGroup,
          dropZone.workflowStepGroupId
        );
      }
      const treeCopy = structuredClone(oldTree);
      const foundNode = findNodeById(treeCopy, dropZone.workflowStepGroupId);
      const foundStep = findStepById(treeCopy, dropZone.workflowStepGroupId);
      if (foundNode) {
        if (dropZone.isParallelStep) {
          if (!foundNode.steps) {
            foundNode.steps = [];
          }
          foundNode.steps.push(step);
        }
      }
      if (foundStep) {
        if (!foundStep.failbackStepGroup && dropZone.isFailback) {
          const failbackGroup = structuredClone(treeToAdd);
          // Set parent to the step's workflowStepGroupId
          failbackGroup.parentWorkflowStepGroupId =
            foundStep.workflowStepGroupId;
          foundStep.failbackStepGroup = failbackGroup;
        }
        if (!foundStep.nestedStepGroup && dropZone.isNested) {
          const nestedGroup = structuredClone(treeToAdd);
          // Set parent to the step's workflowStepGroupId
          nestedGroup.parentWorkflowStepGroupId = foundStep.workflowStepGroupId;
          foundStep.nestedStepGroup = nestedGroup;
        }
      }

      return treeCopy;
    };
    const treeToUpdate = dropZone.isGlobalFailback
      ? failbackStepGroup
      : nextGroup;
    const newTree = mergeTrees(treeToUpdate, updatedGroup);
    if (dropZone.isGlobalFailback) {
      setFailbackStepGroup(newTree);
    } else {
      setNextGroup(newTree);
    }
  };

  return (
    <DndContext
      autoScroll={true}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'inherit',
          maxHeight: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            height: 'inherit',
            maxHeight: 'inherit',
          }}
        >
          <DraggableModules />
          <DroppableCanvas />
        </div>
      </div>

      <DragOverlayModule activeModule={activeModule} />
      <SidePanel />
      <SidePanelForStep />
    </DndContext>
  );
};

export default CreateWorkflowPage;
